import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiClient, buildQueryString, withMinDelayThunk, withMinDelay } from '../../utils/api/';

const DEFAULT_REMINDER_LOG_FILTERS = {
  reminderId: '',
  search: '',
  status: 'ALL',
  type: 'ALL',
};

// Async Thunks

// Get all logs
export const fetchReminderLogs = createAsyncThunk(
  'reminderLogs/fetchReminderLogs',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { initialFetchDone } = getState().reminderLogs;
      const queryString = buildQueryString(params);
      const url = queryString ? `/reminder-logs?${queryString}` : '/reminder-logs';
      const response = !initialFetchDone
        ? await withMinDelay(apiClient.get(url))
        : await apiClient.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch reminder logs');
    }
  }
);

// Delete a reminder log
export const deleteReminderLog = createAsyncThunk(
  'reminderLogs/deleteReminderLog',
  withMinDelayThunk(async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/reminder-logs/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete reminder log');
    }
  })
);

const initialState = {
  items: [],
  pageInfo: null,
  filters: DEFAULT_REMINDER_LOG_FILTERS,
  searchFilterValue: DEFAULT_REMINDER_LOG_FILTERS.search,
  statusFilterValue: DEFAULT_REMINDER_LOG_FILTERS.status,
  typeFilterValue: DEFAULT_REMINDER_LOG_FILTERS.type,
  reminderIdFilterValue: 'ALL',
  loading: false,
  error: null,
  initialFetchDone: false,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
};

const reminderLogsSlice = createSlice({
  name: 'reminderLogs',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      const nextFilters = {
        ...DEFAULT_REMINDER_LOG_FILTERS,
        ...(state.filters || {}),
        ...action.payload,
      };

      state.filters = nextFilters;
      state.searchFilterValue = nextFilters.search;
      state.statusFilterValue = nextFilters.status;
      state.typeFilterValue = nextFilters.type;
      state.reminderIdFilterValue = nextFilters.reminderId || 'ALL';
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Reminder Logs
      .addCase(fetchReminderLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchReminderLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pageInfo = action.payload.pageInfo || null;
        state.initialFetchDone = true;
        state.status = 'succeeded';
      })
      .addCase(fetchReminderLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Delete Reminder Log
      .addCase(deleteReminderLog.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(deleteReminderLog.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item._id !== action.payload);
        state.status = 'succeeded';
      })
      .addCase(deleteReminderLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const {
  clearError,
  resetStatus,
  setFilters
} = reminderLogsSlice.actions;

// Selectors
export const selectAllReminderLogs = (state) => state.reminderLogs.items;
export const selectReminderLogsPageInfo = (state) => state.reminderLogs.pageInfo;
export const selectReminderLogsLoading = (state) => state.reminderLogs.loading;
export const selectReminderLogsInitialFetchDone = (state) => state.reminderLogs.initialFetchDone;
export const selectReminderLogsStatus = (state) => state.reminderLogs.status;
const selectReminderLogsState = (state) => state.reminderLogs;

export const selectReminderLogsFilters = createSelector(
  [selectReminderLogsState],
  (reminderLogsState) => {
    if (reminderLogsState.filters) {
      return reminderLogsState.filters;
    }

    return {
      reminderId: reminderLogsState.reminderIdFilterValue === 'ALL'
        ? DEFAULT_REMINDER_LOG_FILTERS.reminderId
        : reminderLogsState.reminderIdFilterValue,
      search: reminderLogsState.searchFilterValue ?? DEFAULT_REMINDER_LOG_FILTERS.search,
      status: reminderLogsState.statusFilterValue ?? DEFAULT_REMINDER_LOG_FILTERS.status,
      type: reminderLogsState.typeFilterValue ?? DEFAULT_REMINDER_LOG_FILTERS.type,
    };
  }
);

export default reminderLogsSlice.reducer;
